import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Package, AlertTriangle, X, CheckCircle, Droplets, UtensilsCrossed, Ban, Battery, Skull } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for prohibited items
const getProhibitedIcon = (item) => {
  const lower = item.toLowerCase();
  if (lower.includes("liquid")) return Droplets;
  if (lower.includes("food") || lower.includes("perishable")) return UtensilsCrossed;
  if (lower.includes("battery") || lower.includes("lithium")) return Battery;
  if (lower.includes("illegal")) return Skull;
  return Ban;
};

const SplashScreen = ({ onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [settings, setSettings] = useState({
    register_title: "Register Your Package",
    register_text: "To avoid delays in delivery, please register your package in our Customer Portal before it arrives at our warehouse.",
    bp_title: "Don't Forget Your BP Number!",
    bp_number: "BP-137803",
    bp_text: "When shipping, always include this number. Add this to your shipping address to ensure proper routing.",
    prohibited_title: "Prohibited Items",
    prohibited_text: "Please DO NOT ship the following:",
    prohibited_items: ["Liquids", "Perishable Food", "Lithium Battery", "Illegal Items"]
  });

  useEffect(() => {
    // Fetch splash settings from backend
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings/splash`);
        setSettings(res.data);
      } catch (err) {
        console.log("Using default splash settings");
      }
    };
    fetchSettings();

    // Check if user has already seen the splash this session
    const hasSeenSplash = sessionStorage.getItem("hasSeenSplash");
    if (!hasSeenSplash) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.setItem("hasSeenSplash", "true");
    setIsVisible(false);
    if (onClose) onClose();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed inset-0 z-[101] flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 dark:border-slate-700 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 relative">
                <button
                  onClick={handleClose}
                  className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                  data-testid="splash-close-btn"
                >
                  <X className="w-6 h-6" />
                </button>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    <Package className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Welcome to SHIPP SMART!</h2>
                    <p className="text-white/80 text-sm mt-1">Important Information for Your Shipment</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-5">
                {/* Register Package Notice */}
                <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center shrink-0">
                      <CheckCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white text-base">{settings.register_title}</h3>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">
                        {settings.register_text}
                      </p>
                    </div>
                  </div>
                </div>

                {/* BP Number Notice */}
                <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white text-base">{settings.bp_title}</h3>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mt-1">
                        <span className="font-mono font-bold text-blue-600 dark:text-blue-400 text-lg">{settings.bp_number}</span>
                      </p>
                      <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                        {settings.bp_text}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Prohibited Items Notice */}
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center shrink-0">
                      <Ban className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white text-base">{settings.prohibited_title}</h3>
                      <p className="text-slate-600 dark:text-slate-300 text-sm mt-1 mb-2">
                        {settings.prohibited_text}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {settings.prohibited_items.map((item, index) => {
                          const IconComponent = getProhibitedIcon(item);
                          return (
                            <span key={index} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 rounded-full text-xs font-medium">
                              <IconComponent className="w-3.5 h-3.5" /> {item}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 pt-0">
                <Button
                  onClick={handleClose}
                  className="w-full h-12 bg-orange-600 hover:bg-orange-500 text-white font-bold text-base rounded-xl transition-colors"
                  data-testid="splash-understand-btn"
                >
                  I Understand
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SplashScreen;
